"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createCalcAgentMechanics = exports.flattenConcentrationLimits = void 0;
const common_1 = require("@finley-technologies/common");
const concentrationCalcs_1 = require("./concentrationCalcs");
const loanProductMechanics_1 = require("./loanProductMechanics");
const progressHelper_1 = require("./progressHelper");
// flattens the borrowingBaseMechanics into a single array of concentrationLimitConfigs.
// all concentration limit configs are flattened to look like multiLoanProduct concentrationLimitConfigs
const flattenConcentrationLimits = (borrowingBaseMechanics) => {
    const normalizedLimits = [];
    // convert all single loan product concentration limit configs to multi loan product concentration limit configs.
    borrowingBaseMechanics.loanProducts.forEach((loanProductMechanics) => {
        loanProductMechanics.concentrationLimitConfigs.forEach((concentrationLimitConfig) => {
            normalizedLimits.push({
                ...concentrationLimitConfig,
                loanProductIds: [loanProductMechanics.loanProductId],
                isMultiLoanProduct: false,
            });
        });
    });
    // Add multi loan product concentration limits
    borrowingBaseMechanics.multiLoanProductConcentrationLimits.forEach((mlpConcentration) => {
        normalizedLimits.push({ ...mlpConcentration, isMultiLoanProduct: true });
    });
    return normalizedLimits;
};
exports.flattenConcentrationLimits = flattenConcentrationLimits;
// create an interface to evaluate loanProductMechanics.
const createCalcAgentMechanics = ({ config, loanTapeMetadata, f1EngineMode, }) => {
    // create map of loan product id to loan tape mechanics.
    const loanProductMap = new Map(config.loanProducts.map((loanProductMechanics) => [
        loanProductMechanics.loanProductId,
        (0, loanProductMechanics_1.createLoanProductMechanics)({
            config: loanProductMechanics,
            loanTapeMetadata,
            f1EngineMode,
        }),
    ]));
    // this returns the ConcentrationLimitCalculation, but also modifies the input concentrationLimitReceivables
    //  to reflect concentration limit changes.
    const evaluateConcentrationAndMutateReceivables = async ({ concentrationLimitReceivables, progressCallback, receivableWaivers, }) => {
        const normalizedLoanProductConcentrationLimits = (0, exports.flattenConcentrationLimits)(config);
        const loanProductIds = config.loanProducts.map(({ loanProductId }) => loanProductId);
        return (0, concentrationCalcs_1.evaluateNormalizedLoanProductConcentrationAndMutateReceivables)({
            concentrationLimitReceivables,
            normalizedLoanProductConcentrationLimits,
            loanProductIds,
            loanTapeMetadata,
            concentrationKickoutMethod: config._config_.concentrationKickoutMethod,
            progressCallback,
            receivableWaivers,
            f1EngineMode,
        });
    };
    const getLoanProductMechanics = (loanProductId) => {
        const loanProductMechanics = loanProductMap.get(loanProductId);
        (0, common_1.finleyAssert)(loanProductMechanics, `loanProductMechanics not found for: [${loanProductId}]`);
        return loanProductMechanics;
    };
    const evaluateValuationRate = (creditFacilityReceivable) => {
        return getLoanProductMechanics(creditFacilityReceivable.loanProductId).evaluateValuationRate(creditFacilityReceivable);
    };
    const evaluatePurchasePercentageRate = (creditFacilityReceivable) => {
        return getLoanProductMechanics(creditFacilityReceivable.loanProductId).evaluatePurchasePercentageRate(creditFacilityReceivable);
    };
    const evaluateEligibility = ({ valuationReceivable, waivedEligibilityTestKeysForReceivable, }) => {
        return getLoanProductMechanics(valuationReceivable.loanProductId).evaluateEligibility({
            valuationReceivable,
            waivedEligibilityTestKeysForReceivable,
        });
    };
    const evaluateAdvanceRate = async (params) => {
        const { concentrationLimitReceivables, progressCallback } = params;
        const lpIdToReceivablesMap = new Map();
        for (const { loanProductId } of config.loanProducts) {
            lpIdToReceivablesMap.set(loanProductId, []);
        }
        for (const receivable of concentrationLimitReceivables) {
            if (!lpIdToReceivablesMap.has(receivable.loanProductId)) {
                lpIdToReceivablesMap.set(receivable.loanProductId, []);
            }
            const receivableArray = lpIdToReceivablesMap.get(receivable.loanProductId);
            (0, common_1.finleyAssert)(receivableArray, `evaluateAdvanceRate: failed to find receivables when setting [loanProductId:${receivable.loanProductId}]`);
            receivableArray.push(receivable);
        }
        const allBorrowingBaseReceivables = [];
        const loanProductWeights = new Array(config.loanProducts.length).fill(1);
        const loanProductCallbacks = (0, progressHelper_1.createPartialProgressCallbacks)({
            progressCallback,
            weights: loanProductWeights,
        });
        for (const [index, { loanProductId }] of config.loanProducts.entries()) {
            const loanProductMechanics = getLoanProductMechanics(loanProductId);
            (0, common_1.finleyAssert)(loanProductMechanics.configs.advanceRate.loanProductIds.length > 0, `Came across 0 configured loan products for advance rate evaluation [loanProductId:${loanProductId}]`);
            // "targetReceivables" represent the receivables associated with the target loan product,
            // these are the ones that actually need to be updated for this loop iteration.
            // "calculationReceivables" represent all the receivables needed in order to help
            // perform the actual calculations, as advance rates can be evaluated across multi-loan products.
            let targetReceivables = [];
            const calculationReceivables = [];
            loanProductMechanics.configs.advanceRate.loanProductIds.forEach((lpId) => {
                const lpReceviables = lpIdToReceivablesMap.get(lpId);
                (0, common_1.finleyAssert)(lpReceviables, `evaluateAdvanceRate: failed to find receivables [loanProductId:${lpId}`);
                for (const receivable of lpReceviables) {
                    calculationReceivables.push(receivable);
                }
                if (lpId === loanProductId) {
                    targetReceivables = lpReceviables;
                }
            });
            if (targetReceivables.length === 0 ||
                calculationReceivables.length === 0) {
                continue;
            }
            // eslint-disable-next-line no-await-in-loop
            const receivableAdvanceRateMap = await loanProductMechanics.evaluateAdvanceRate({
                targetReceivables,
                calculationReceivables,
                progressCallback: loanProductCallbacks[index],
            });
            for (const receivable of targetReceivables) {
                const advanceRateResult = receivableAdvanceRateMap[receivable.finleyReceivableId];
                const borrowingBaseAmount = advanceRateResult.advanceRate *
                    receivable.concentrationLimitFields
                        .eligibleExposureLessConcentrationLimitKickouts;
                allBorrowingBaseReceivables.push({
                    ...receivable,
                    borrowingBaseFields: {
                        advanceRate: advanceRateResult.advanceRate,
                        advanceRatePath: advanceRateResult.path,
                        borrowingBaseAmount,
                    },
                });
            }
        }
        return allBorrowingBaseReceivables;
    };
    return {
        getLoanProductMechanics,
        evaluateConcentrationAndMutateReceivables,
        evaluateValuationRate,
        evaluatePurchasePercentageRate,
        evaluateEligibility,
        evaluateAdvanceRate,
    };
};
exports.createCalcAgentMechanics = createCalcAgentMechanics;
//# sourceMappingURL=calcAgentMechanics.js.map